import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import api, { dateShort, timeAgo, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionTitle, Progress, Thumb, EmptyState, Cover } from "@/components/Bits";

const Stat = ({ label, value, sub, testId, accent }) => (
  <div data-testid={testId} className="card-surface card-hover p-6">
    <div className="overline">{label}</div>
    <div className={`num mt-4 text-3xl font-medium ${accent ? "text-lime-dark" : "text-ink"}`}>{value}</div>
    {sub && <div className="mt-2 text-xs text-fog">{sub}</div>}
  </div>
);

export default function ClientDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [presentations, setPresentations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const show = (key) => (data?.sections || {})[key] !== false;

  useEffect(() => {
    api.get("/client/dashboard").then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/presentations").then((r) => setPresentations(r.data)).catch(() => setPresentations([]));
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => setInvoices([]));
  }, []);

  const firstName = (user?.name || "there").split(" ")[0];
  const s = data?.summary || {};
  const projects = data?.projects || [];
  const multi = projects.length > 1;
  const needs = data?.needs_attention || [];
  const totalPending = needs.reduce((a, n) => a + (n.count || 0), 0);
  const decisionsPending = s.decisions_needing_review ?? totalPending;

  return (
    <div className="space-y-14" data-testid="client-dashboard-page">
      <PageHeader
        overline="Your Design Portal"
        title={`Good morning, ${firstName}`}
        subtitle={
          multi
            ? "Your projects are moving along. Here’s the latest across each one."
            : "Your home is coming together. Here’s the latest on your project."
        }
      />

      {show("timeline") && (
      <section>
        <SectionTitle
          action={
            <Link to="/calendar" data-testid="client-view-calendar-link" className="text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">
              Full timeline
            </Link>
          }
        >
          Coming Up
        </SectionTitle>
        {(data?.coming_up || []).length === 0 ? (
          <div className="card-surface p-7 text-sm text-fog" data-testid="client-coming-up-empty">
            No dates are confirmed just yet — we’ll add them here as they’re set.
          </div>
        ) : (
          <div className="card-surface" data-testid="client-coming-up">
            {(data.coming_up || []).map((e) => (
              <Link
                key={e.id}
                to="/calendar"
                data-testid={`coming-up-${e.id}`}
                className="flex flex-wrap items-center gap-5 border-b border-line px-6 py-5 transition-colors last:border-0 hover:bg-bone"
              >
                <div className="w-16 shrink-0">
                  <div className="overline">{new Date(e.start).toLocaleDateString([], { month: "short" })}</div>
                  <div className="num text-2xl text-ink">{new Date(e.start).getDate()}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-serif text-lg text-ink">{e.title}</div>
                  <div className="mt-1 text-xs text-fog">
                    {e.event_type}
                    {e.all_day ? " · All day" : ` · ${new Date(e.start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`}
                  </div>
                </div>
                <span className="text-xs uppercase tracking-[0.15em] text-lime-dark">View →</span>
              </Link>
            ))}
          </div>
        )}
      </section>
      )}

      {show("decisions") && (
      <section>
        <SectionTitle
          action={
            <Link to="/decisions" data-testid="client-view-decisions-link" className="text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">
              All decisions
            </Link>
          }
        >
          Needs Your Attention
        </SectionTitle>
        {totalPending === 0 ? (
          <div className="card-surface flex flex-col items-start gap-3 p-8" data-testid="client-all-caught-up">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#EDEFC4]">
              <Check className="h-5 w-5 text-lime-dark" strokeWidth={1.5} />
            </span>
            <h3 className="font-serif text-2xl text-ink">You’re all caught up.</h3>
            <p className="text-sm text-stone">There are currently no decisions waiting for you.</p>
          </div>
        ) : (
          <div className="space-y-6">
            <Link
              to="/decisions"
              data-testid="client-decisions-summary-card"
              className="card-surface card-hover group flex flex-wrap items-center justify-between gap-5 p-7"
            >
              <div>
                <h3 className="font-serif text-2xl text-ink">
                  {decisionsPending} Decision{decisionsPending === 1 ? "" : "s"} Need Your Attention
                </h3>
                <p className="mt-2 text-sm text-stone">
                  {needs[0]?.title ? `Starting with ${needs[0].title}.` : "Review them all in one place."}
                </p>
              </div>
              <span className="inline-flex items-center gap-2 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink transition-colors group-hover:bg-lime-dark">
                Review Decisions
                <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </span>
            </Link>
            <div className="grid gap-6 lg:grid-cols-2" data-testid="client-needs-attention">
            {needs.filter((n) => n.count > 0).map((n) => (
              <Link
                key={`${n.type}-${n.id}`}
                to={n.link || (n.type === "presentation" ? `/presentations/${n.id}` : "/decisions")}
                data-testid={`needs-attention-${n.type}-${n.id || "loose"}`}
                className="card-surface card-hover group flex items-stretch overflow-hidden"
              >
                {n.hero_image ? (
                  <img src={n.hero_image} alt="" className="hidden w-40 shrink-0 object-cover sm:block" />
                ) : null}
                <div className="flex flex-1 flex-col justify-between gap-4 p-6">
                  <div>
                    <h3 className="font-serif text-2xl leading-snug text-ink">
                      {n.type === "questions" || n.type === "options"
                        ? n.title
                        : `${n.count} Selection${n.count === 1 ? "" : "s"} Need${n.count === 1 ? "s" : ""} Your Approval`}
                    </h3>
                    <p className="mt-2 text-sm text-stone">
                      {n.type === "questions"
                        ? "Your designer is waiting on your answer."
                        : n.type === "options"
                        ? `${n.count} directions to compare — pick the one you love.`
                        : `Review the latest selections for your ${n.space_name || n.title}.`}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-lime-dark">
                    {n.type === "presentation"
                      ? "Review Presentation"
                      : n.type === "options"
                      ? "Compare Options"
                      : "Review Selections"}
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={1.5} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
          </div>
        )}
      </section>
      )}

      <section>
        <SectionTitle>Project Progress</SectionTitle>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <Stat testId="client-metric-progress" label="Project Progress" value={`${s.progress || 0}% Complete`} accent />
          {show("timeline") && (
            <Stat
              testId="client-metric-milestone"
              label="Next Milestone"
              value={s.next_milestone || "—"}
              sub={dateShort(s.next_milestone_date)}
            />
          )}
          {show("decisions") && (
            <Stat testId="client-metric-approval" label="Decisions Needing You" value={decisionsPending} />
          )}
          {show("selections") && (
            <Stat testId="client-metric-on-the-way" label="Items On the Way" value={s.on_the_way ?? 0} />
          )}
        </div>
      </section>

      {show("selections") && (
      <section>
        <SectionTitle>Your Selections</SectionTitle>
        <div className="card-surface grid grid-cols-2 divide-line sm:grid-cols-5" data-testid="client-selection-summary">
          {[
            ["Total Selections", s.total_selections ?? 0, null],
            ["Approved", s.approved ?? 0, null],
            ["Need Your Approval", s.awaiting_approval ?? 0, "/decisions"],
            ["On the Way", s.on_the_way ?? 0, null],
            ["Delivered", s.delivered ?? 0, null],
          ].map(([label, value, link]) =>
            link ? (
              <Link key={label} to={link} data-testid="client-summary-awaiting" className="border-b border-r border-line p-6 transition-colors last:border-r-0 hover:bg-bone">
                <div className="num text-2xl text-ink">{value}</div>
                <div className="overline mt-2 leading-tight">{label}</div>
              </Link>
            ) : (
              <div key={label} className="border-b border-r border-line p-6 last:border-r-0">
                <div className="num text-2xl text-ink">{value}</div>
                <div className="overline mt-2 leading-tight">{label}</div>
              </div>
            )
          )}
        </div>
      </section>
      )}

      {show("invoices") && (
      <section>
        <SectionTitle
          action={
            <Link to="/invoices" data-testid="client-view-invoices-link" className="text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">
              All invoices
            </Link>
          }
        >
          Payments
        </SectionTitle>
        {invoices.filter((i) => i.balance > 0 && ["Sent", "Partially Paid", "Overdue"].includes(i.status)).length === 0 ? (
          <div className="card-surface p-7 text-sm text-fog" data-testid="client-no-open-invoices">
            Nothing is due right now — you’re all settled up.
          </div>
        ) : (
          <div className="card-surface" data-testid="client-open-invoices">
            {invoices.filter((i) => i.balance > 0 && ["Sent", "Partially Paid", "Overdue"].includes(i.status)).map((i) => (
              <Link
                key={i.id}
                to={`/invoices/${i.id}`}
                data-testid={`client-invoice-${i.id}`}
                className="flex flex-wrap items-center gap-5 border-b border-line px-6 py-5 transition-colors last:border-0 hover:bg-bone"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-serif text-lg text-ink">{i.title}</div>
                  <div className="num mt-1 text-xs text-fog">
                    {i.number} · {i.project_name}{i.due_date ? ` · Due ${dateShort(i.due_date)}` : ""}
                  </div>
                </div>
                <div className="num text-lg text-ink">{money(i.balance)}</div>
                <span className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2 text-sm font-medium text-ink">Pay now</span>
              </Link>
            ))}
          </div>
        )}
      </section>
      )}

      <section>
        <SectionTitle>Your Project{multi ? "s" : ""}</SectionTitle>
        {projects.length === 0 ? (
          <EmptyState testId="client-no-projects" title="No projects yet" body="Your designer will share your project here." />
        ) : (
          <div className="grid gap-7 md:grid-cols-2 xl:grid-cols-3">
            {projects.map((p, i) => (
              <Link key={p.id} to={`/projects/${p.id}`} data-testid={`client-project-card-${p.id}`} className="card-surface card-hover group animate-rise overflow-hidden" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="aspect-[16/10] overflow-hidden bg-line">
                  {p.cover_file_id || p.cover_image ? (
                    <Cover project={p} className="h-full w-full" imgClassName="transition-transform duration-500 group-hover:scale-[1.03]" />
                  ) : (
                    <Thumb className="h-full w-full" rounded="rounded-none" />
                  )}
                </div>
                <Progress value={p.progress} />
                <div className="p-6">
                  <h3 className="font-serif text-xl text-ink">{p.name}</h3>
                  <div className="mt-2 text-sm text-stone">{p.phase}</div>
                  <div className="mt-4 text-xs text-fog">
                    {p.install_date ? `Installation · ${dateShort(p.install_date)}` : `Target · ${dateShort(p.target_completion_date)}`}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {show("presentations") && presentations.length > 0 && (
        <section>
          <SectionTitle>Your Designs</SectionTitle>
          <div className="grid gap-7 md:grid-cols-2 xl:grid-cols-3" data-testid="client-presentations">
            {presentations.map((p, i) => (
              <Link
                key={p.id}
                to={`/presentations/${p.id}`}
                data-testid={`client-presentation-card-${p.id}`}
                className="card-surface card-hover group animate-rise overflow-hidden"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="aspect-[16/10] overflow-hidden bg-line">
                  {p.hero_image ? (
                    <img src={p.hero_image} alt={p.title || p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                  ) : (
                    <Thumb className="h-full w-full" rounded="rounded-none" />
                  )}
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-xl text-ink">{p.title || p.name}</h3>
                  <div className="mt-2 text-sm text-stone">{p.space_name || p.project_name}</div>
                  <div className="num mt-4 text-xs text-fog">
                    {p.selection_count > 0 ? `${p.selection_count} selections` : "View presentation"}
                    {p.awaiting_count ? ` · ${p.awaiting_count} need your approval` : ""}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {show("activity") && (
      <section>
        <SectionTitle>Latest Update</SectionTitle>
        <div className="card-surface divide-y divide-line" data-testid="client-latest-update">
          {(data?.activity || []).map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
              <div className="text-sm text-ink">{a.client_action || a.detail || a.action}</div>
              <div className="flex items-center gap-4 text-xs text-fog">
                <span>{a.project_name}</span>
                <span className="num">{dateShort(a.created_at)} · {timeAgo(a.created_at)}</span>
              </div>
            </div>
          ))}
          {!data?.activity?.length && <div className="px-6 py-10 text-center text-sm text-fog">No updates yet.</div>}
        </div>
      </section>
      )}
    </div>
  );
}
